import { Component, Input, Output, EventEmitter, OnChanges, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { UnderfrequencyActionModal } from '../modals/underfrequency-action-modal/underfrequency-action-modal.component';
import { UnderfrequencyBulkRestoreModal } from '../modals/underfrequency-bulk-restore-modal/underfrequency-bulk-restore-modal.component';
import { ACTION, DEVICE_STATUS, UnderfrequencyPayload } from '../../services/underfrequency-event.service';
import { environment } from '../../../environments/environment';

interface FieldMeta {
    display: string;
    value: string;
}

@Component({
  standalone: false,
    selector: 'app-underfrequency-table',
    templateUrl: './underfrequency-table.component.pug',
    styleUrls: ['./underfrequency-table.component.scss']
})
export class UnderfrequencyTableComponent implements OnChanges {

    constructor(
        public dialog: MatDialog,
        private toastr: ToastrService,
    ) {}

    @Input() header: string;
    @Input() placeholder: string;
    @Input() rows: UnderfrequencyPayload[];
    @Input() excludeFields: string[];
    @Input() showBulkRestore = false;
    @Output() refresh: EventEmitter<boolean> = new EventEmitter<boolean>();

    bulkRestoreActive = false;
    selectedRows = new Set<string>();

    @ViewChild(MatSort, {static: false}) sort: MatSort;
    dataSource: MatTableDataSource<UnderfrequencyPayload>;
    filter: string = '';
    aunt_url = environment.aunt_url;

    allFields: FieldMeta[] = [
        {display: 'Program', value: 'program_display_label'},
        {display: 'Registration Set', value: 'registration_set_display_label'},
        {display: 'Last Updated', value: 'last_updated_dttm'},
        {display: 'Disarm Reason', value: 'disarm_reason'},
        {display: 'Offer', value: 'offer'}, // Needs source
        {display: 'Availability', value: 'availability'}, // Needs source
        {display: 'Load', value: 'load'}, // Needs source
        {display: 'Device Status', value: 'device_status'},
        {display: 'Frequency', value: 'actual_frequency'}, // Needs source
        {display: 'Frequency Setpoint', value: 'frequency_setpoint'}, // Needs source
        {display: 'Event Details', value: 'event_details_link'},
        {display: 'Action', value: 'action'}, // Needs clarity
        {display: 'Info', value: 'info'},
    ];
    fields: FieldMeta[];
    displayedFields: string[];

    ngOnInit() {
        this.fields = this.allFields.filter(f => !this.excludeFields.includes(f.value));
        this.displayedFields = this.fields.map(f => f.value);
    }

    ngOnChanges() {
        this.dataSource = new MatTableDataSource(this.rows);
        this.filterData();
        setTimeout(() => this.dataSource.sort = this.sort, 50);
    }

    filterData(_ev?: any) {
        this.dataSource.filter = this.filter.toLowerCase().trim();
    }

    clickAction(row: UnderfrequencyPayload, actionOverride: ACTION) {
        const dialogRef = this.dialog.open(UnderfrequencyActionModal, {
            data: {
                ...row,
                action: actionOverride,
            },
        });

        dialogRef.afterClosed().subscribe(() => {
            setTimeout(() => this.refresh.emit(true), 400);
        });
    }

    showMoreInfo(row: UnderfrequencyPayload) {
      const url = window.location.origin + '/underfrequency-management/' + row.registration_set_id;
      window.open(url, '_blank');
    }

    get activeDisplayedFields(): string[] {
        return this.bulkRestoreActive ? ['select', ...this.displayedFields] : this.displayedFields;
    }

    get selectableFilteredRows(): UnderfrequencyPayload[] {
        return (this.dataSource?.filteredData || []).filter(r => this.isSelectable(r));
    }

    get selectedCount(): number {
        return this.selectedRows.size;
    }

    isSelectable(row: UnderfrequencyPayload): boolean {
        return row.device_status === DEVICE_STATUS.TRIPPED;
    }

    isSelected(row: UnderfrequencyPayload): boolean {
        return this.selectedRows.has(row.event_node_id);
    }

    isAllSelected(): boolean {
        const selectable = this.selectableFilteredRows;
        return selectable.length > 0 && selectable.every(r => this.selectedRows.has(r.event_node_id));
    }

    toggleRow(row: UnderfrequencyPayload): void {
        if (this.selectedRows.has(row.event_node_id)) {
            this.selectedRows.delete(row.event_node_id);
        } else {
            this.selectedRows.add(row.event_node_id);
        }
        // trigger change detection on the Set reference
        this.selectedRows = new Set(this.selectedRows);
    }

    toggleSelectAll(): void {
        if (this.isAllSelected()) {
            this.selectableFilteredRows.forEach(r => this.selectedRows.delete(r.event_node_id));
        } else {
            this.selectableFilteredRows.forEach(r => this.selectedRows.add(r.event_node_id));
        }
        this.selectedRows = new Set(this.selectedRows);
    }

    enterBulkMode(): void {
        this.bulkRestoreActive = true;
        this.selectedRows = new Set();
    }

    cancelBulkMode(): void {
        this.bulkRestoreActive = false;
        this.selectedRows = new Set();
    }

    openBulkRestoreModal(): void {
        const selectedPayloads = (this.dataSource?.filteredData || [])
            .filter(r => this.selectedRows.has(r.event_node_id));

        const dialogRef = this.dialog.open(UnderfrequencyBulkRestoreModal, {
            data: selectedPayloads,
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result?.success) {
                this.toastr.success('Successfully restored the selected registration sets');
                this.cancelBulkMode();
                setTimeout(() => this.refresh.emit(true), 400);
            }
        });
    }
}
